import React, { useEffect, useState } from 'react';

import DateFnsUtils from '@date-io/date-fns';
import { Box, InputAdornment } from '@material-ui/core';
import EventIcon from '@material-ui/icons/Event';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { format, parseISO } from 'date-fns';
import i18n from 'i18n-js';

import {
    DATE_FORMAT,
    formatAssignedTables,
    formatGuestName,
    formatGuestsCount,
    formatReservationDate,
    getDateFnsLocale,
} from 'helpers/reservationHelper';
import { useAppSelector } from 'index';
import reservationsStore from 'store/reservationsStore';
import { useReservationMaps, useThunkDispatch } from '../hooks';
import ReservationDetailsPanel, { ReservationStatusChip } from './ReservationDetailsPanel';
import ReservationsTable, { ReservationColumn } from './ReservationsTable';

const DATE_FIELD_WIDTH = 220;

export default function ActiveTab({ initialDate }: { initialDate?: string }) {
    const dispatch = useThunkDispatch();
    const { day, selectedActiveId } = useAppSelector((state) => state.reservations);
    const { tables } = useReservationMaps();
    const [date, setDate] = useState(initialDate ?? format(new Date(), DATE_FORMAT));
    const reservations = day.date === date ? day.reservations : [];
    const selected = reservations.find((reservation) => reservation.id === selectedActiveId);

    useEffect(() => {
        dispatch(reservationsStore.actionCreators.getDayReservations(date));
    }, [date]); // eslint-disable-line react-hooks/exhaustive-deps

    const columns: ReservationColumn[] = [
        {
            id: 'dateTime',
            label: i18n.t('reservations.columns.dateTime'),
            getText: (reservation) =>
                formatReservationDate(reservation.reservationStartDateTime, 'dd.MM · HH:mm'),
            getSortValue: (reservation) => reservation.reservationStartDateTime,
        },
        { id: 'name', label: i18n.t('reservations.columns.name'), getText: formatGuestName },
        {
            id: 'guests',
            label: i18n.t('reservations.columns.guests'),
            getText: formatGuestsCount,
            getSortValue: (reservation) => reservation.guestsNumber,
        },
        {
            id: 'table',
            label: i18n.t('reservations.columns.table'),
            getText: (reservation) => formatAssignedTables(reservation.tables, tables),
        },
        {
            id: 'channel',
            label: i18n.t('reservations.columns.channel'),
            getText: (reservation) => i18n.t(`reservations.channels.${reservation.channel}`),
        },
        {
            id: 'status',
            label: i18n.t('reservations.columns.status'),
            getText: (reservation) =>
                i18n.t(`reservations.status.${reservation.status.toLowerCase()}`),
            render: (reservation) => <ReservationStatusChip status={reservation.status} />,
        },
    ];

    return (
        <Box display="flex" flexDirection="column" flex={1} minHeight={0} pl={3}>
            <Box pb={2}>
                <MuiPickersUtilsProvider utils={DateFnsUtils} locale={getDateFnsLocale()}>
                    <DatePicker
                        variant="inline"
                        inputVariant="filled"
                        autoOk
                        format="EEE, dd.MM.yyyy"
                        label={i18n.t('reservations.fields.date')}
                        value={parseISO(date)}
                        onChange={(value) => value && setDate(format(value as Date, DATE_FORMAT))}
                        style={{ width: DATE_FIELD_WIDTH }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <EventIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                </MuiPickersUtilsProvider>
            </Box>
            <Box display="flex" flex={1} minHeight={0}>
                <ReservationsTable
                    columns={columns}
                    reservations={reservations}
                    selectedId={selectedActiveId}
                    onSelect={(id) => dispatch(reservationsStore.actionCreators.selectActive(id))}
                />
                {selected && (
                    <ReservationDetailsPanel
                        reservation={selected}
                        tables={tables}
                        onClose={() =>
                            dispatch(reservationsStore.actionCreators.selectActive(null))
                        }
                    />
                )}
            </Box>
        </Box>
    );
}
